import { publishButtonState } from '../../lib/publish-button.js';
import type { ListingStatus } from './listing-detail-pane.js';

// ============================================================================
// Abschnitt „Deine Verkaufsbedingungen": geteilte Bausteine
// ----------------------------------------------------------------------------
// Dieselben acht Felder werden an ZWEI Stellen gebraucht:
//   * im Aufschalt-Panel (mode='attach', renderAttachPanel),
//   * im Ueberblick-Tab (mode='new', renderNewOfferSection).
// Zwei Kopien derselben Feldliste laufen garantiert auseinander, und die
// snake-case-Keys muessen exakt zu dem passen, was der Worker liest
// (apps/worker/src/jobs/kaufland-publish-listing.ts, Abschnitt 4):
//   condition, listing_price (Cent), amount, handling_time, id_shipping_group,
//   vat_indicator, minimum_price (Cent), note.
//
// Schnitt: Dieses Modul ist netzfrei und bis auf zwei duenne Leser auch DOM-frei.
// Die Vorbefuellung aus Vario (`resolveVarioOfferValues`) und der Supabase-Client
// bleiben beim Aufrufer; hier landet nur, was sich ohne Browser und ohne Backend
// pruefen laesst. Deshalb bekommt saveAndEnqueueOffer() seine beiden RPCs
// injiziert, statt den Client zu kennen.
//
// `import type` auf ListingStatus ist bei verbatimModuleSyntax vollstaendig
// geloescht, es entsteht also KEIN Laufzeit-Zyklus mit listing-detail-pane.ts.
// Dasselbe Muster nutzt lib/publish-button.ts.
// ============================================================================

/**
 * Kauflands Obergrenze fuer `UnitRequest.note` (Seller-API-Spec, maxLength 250). Das Feld ist
 * KEIN interner Vermerk: Kaufland zeigt den Text auf der Produktseite, bei gebrauchten
 * Artikeln (Kondition ungleich NEW). Die Grenze steht hier, damit Textfeld und Pruefung
 * denselben Wert nutzen und ein zu langer Text nicht erst am Push scheitert.
 */
export const NOTE_MAX_LENGTH = 250;

export interface OfferDefaults {
  condition: string | null;
  handling_time: number | null;
  id_shipping_group: string | null;
  vat_indicator: string | null;
  field_mappings?: Record<string, unknown> | null;
}

export interface ShippingGroupRow {
  id_shipping_group: string;
  name: string | null;
}

export interface VatIndicatorRow {
  vat_indicator: string;
  label: string | null;
  vat_rate: number | null;
}

/** Ergebnis der Vario-Aufloesung aus listing-detail-pane.resolveVarioOfferValues().
 *  Liegt hier, weil buildOfferFormPrefill() es entgegennimmt und die Aufloesung
 *  selbst (Edge Function vario-mapping-sources) beim Aufrufer bleibt. */
export interface ResolvedVarioValues {
  /** Listing-Preis in EUR (decimal), fuer das EUR-Input direkt einsetzbar. */
  listingPriceEur: number | null;
  /** Lagerbestand (integer). */
  amount: number | null;
  /** Mindestpreis in EUR (decimal). */
  minimumPriceEur: number | null;
  /** Von der Vario-Cascade aufgeloester MwSt-Indikator (article.taxSchemaRef →
   *  tax-rates → kaufland.vat_indicators-Match). null wenn nicht aufloesbar. */
  vatIndicator: string | null;
  /** MwSt-Rate als Prozentzahl (z.B. 19), nur zur Anzeige im Hint. */
  vatRate: number | null;
  /** Hint-Strings pro Feld (welcher Vario-Pfad genutzt wurde). */
  hints: Partial<Record<'listing_price' | 'amount' | 'minimum_price', string>>;
  /**
   * Bestandskaskade `auto.stock_available` roh (seit 2026-09-12): die Summe von
   * `stockAvailable` ueber die in den Kaufland-Einstellungen zugeordneten Lager, plus Grund
   * und Lagerangabe fuer den Klicktext „N verfügbar übernehmen". Unabhaengig vom Mapping,
   * das `amount` speist -- das darf auf eine andere Quelle zeigen.
   */
  autoStock?: {
    value: number | null;
    reason: string | null;
    storageLabel: string | null;
    storageCount: number;
  };
}

export const CONDITION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'NEW',          label: 'Neu' },
  { value: 'LIKE_NEW',     label: 'Wie neu' },
  { value: 'VERY_GOOD',    label: 'Sehr gut' },
  { value: 'GOOD',         label: 'Gut' },
  { value: 'ACCEPTABLE',   label: 'Akzeptabel' },
  { value: 'REFURBISHED',  label: 'Refurbished' },
];

/** Marker am <form>. Beide Aufrufer setzen ihn, der Kopfleisten-Knopf findet das
 *  Formular darueber, ohne die beiden verschiedenen IDs kennen zu muessen. */
export const OFFER_FORM_SELECTOR = 'form[data-offer-form]';

// ---------------------------------------------------------------------------
// Bearbeitbarkeit
// ---------------------------------------------------------------------------

/**
 * Darf das Formular bearbeitet und gespeichert werden?
 *
 * Bewusst eine ===-Kette und kein Record<ListingStatus, boolean>: der Typecheck kann
 * einen fehlenden Status hier NICHT anmahnen. Bei jedem neuen Status pruefen, ob das
 * Formular in diesem Zustand bearbeitbar sein darf, und die Liste manuell nachziehen.
 *
 * Die drei gesperrten Zustaende sind die, in denen bei Kaufland bereits etwas laeuft:
 * der Push ist eingereiht, die Pruefung laeuft, oder das Angebot ist aktiv.
 *
 * Bearbeiten ist NICHT dasselbe wie Aufschalten. 'archived' etwa bleibt hier
 * bearbeitbar, darf aber nicht abgeschickt werden. Diese zweite Frage beantwortet
 * publishButtonState(); die Aufrufer pruefen beides und sperren den Knopf getrennt
 * von den Feldern.
 */
export function isOfferFormEditable(status: ListingStatus): boolean {
  if (status === 'queued') return false;
  if (status === 'published') return false;
  if (status === 'validating') return false;
  return true;
}

/**
 * Darf der Abschnitt abgeschickt werden, also gespeichert UND eingereiht?
 *
 * Zwei Fragen in einer, weil ein Knopf nur eine Antwort anzeigen kann:
 *   * sind die Felder ueberhaupt bearbeitbar (isOfferFormEditable), und
 *   * laesst der Zustand ein Aufschalten zu (publishButtonState).
 * Der zweite Teil faengt 'archived' ab: dort darf man pflegen, aber nicht listen.
 * Ohne ihn liefe der Klick in die rohe Backend-Meldung von
 * kaufland_save_attach_overrides, das diesen Status nicht zulaesst.
 *
 * `reason` taugt direkt als title am gesperrten Knopf.
 */
export function offerSubmitGate(
  status: ListingStatus,
  mode: 'new' | 'attach' | 'undecided' | 'unknown',
): { allowed: boolean; reason: string } {
  const pub = publishButtonState({ status, mode });
  // 'validating' braucht seit dem 2026-08-07 eine eigene Begruendung. Der Kopfleisten-Knopf
  // ist dort jetzt FREI (erneut einreichen, siehe publish-button.ts), dieses Formular aber
  // nicht: `kaufland_save_attach_overrides` laesst den Status nicht zu (Migration 0029), ein
  // Klick liefe also in eine rohe Backend-Meldung. Ohne diesen Zweig stuende am gesperrten
  // Knopf der Tooltip des freigegebenen -- "Produktdaten erneut einreichen" an etwas, das
  // gerade nichts tut.
  if (status === 'validating') {
    return { allowed: false, reason: 'Während Kauflands Prüfung nicht änderbar' };
  }
  // Seit dem 2026-08-08 ist der Kopfleisten-Knopf auch bei 'published' frei ("Erneut
  // senden"). Dieses Formular bleibt es nicht: Verkaufspreis und Bestand laufen dann ueber
  // den Angebote-Tab. Ohne eigenen Zweig stuende am gesperrten Knopf der Tooltip des freien.
  if (status === 'published') {
    return { allowed: false, reason: 'Bedingungen verwaltest Du im Tab „Angebote"' };
  }
  return { allowed: pub.enabled && isOfferFormEditable(status), reason: pub.title };
}

// ---------------------------------------------------------------------------
// Vorbefuellung
// ---------------------------------------------------------------------------

export interface OfferFormPrefillInput {
  /** snapshot.offerOverrides, leeres Objekt wenn noch nie gespeichert. */
  existingOverrides: Record<string, unknown>;
  /** Ergebnis von kaufland_resolve_offer_defaults. */
  defaults: OfferDefaults;
  /** Ergebnis von resolveVarioOfferValues. */
  vario: ResolvedVarioValues;
  /** Storefront-Liste, nur um den Vario-Vorschlag gegenzupruefen. */
  vatIndicators: readonly VatIndicatorRow[];
}

export interface OfferFormPrefill {
  condition: string;
  /** Als String, weil er so ins value-Attribut geht. Leer heisst "nichts vorbelegt". */
  listingPriceEuro: string;
  amount: string;
  handlingTime: string;
  idShippingGroup: string;
  vatIndicator: string;
  /** true, wenn der MwSt-Wert aus dem Vario-Steuerschema kam (steuert den Hint). */
  vatAutoChosen: boolean;
  /** Rate zum Vario-Vorschlag, nur fuer den Hint-Text. */
  vatAutoRate: number | null;
  minimumPriceEuro: string;
  note: string;
  /** „Aus Vario: …"-Zeile pro Feld, nur wenn der Wert NICHT aus einem
   *  gespeicherten Override stammt. */
  hints: { listing_price?: string; amount?: string; minimum_price?: string };
}

/**
 * Pre-Fill-Prioritaet, hoechste zuerst:
 *   1. gespeicherte offerOverrides (Cent → /100 fuer die EUR-Felder)
 *   2. aus Vario aufgeloest (EUR / Integer, via vario-mapping-sources)
 *   3. leer, der Haendler traegt von Hand ein
 *
 * Fuer Kondition, Lieferzeit, Versandgruppe und MwSt gilt dieselbe Reihenfolge mit
 * dem Offer-Default-Profil auf Stufe 2. Der MwSt-Vorschlag aus Vario wird nur
 * uebernommen, wenn der Code in der Storefront-Liste wirklich existiert; sonst
 * stuenden im Select Werte, die Kaufland nicht kennt.
 */
export function buildOfferFormPrefill(input: OfferFormPrefillInput): OfferFormPrefill {
  const { existingOverrides, defaults, vario, vatIndicators } = input;

  const condition = pickStringOverride(existingOverrides, 'condition') ?? defaults.condition ?? 'NEW';
  const handlingTime = pickNumberOverride(existingOverrides, 'handling_time') ?? defaults.handling_time ?? 2;
  const idShippingGroup = pickStringOverride(existingOverrides, 'id_shipping_group') ?? defaults.id_shipping_group ?? '';

  // VAT-Aufloesung (Reihenfolge):
  //   1. Override aus snapshot.offerOverrides (Haendler hat explizit gepflegt)
  //   2. Profil-Default aus kaufland.offer_default_profiles
  //   3. Vario-Cascade: article.taxSchemaRef -> tax-rates -> kaufland.vat_indicators
  //   4. leer lassen, der Haendler muss waehlen
  let vatIndicator = pickStringOverride(existingOverrides, 'vat_indicator') ?? defaults.vat_indicator ?? '';
  let vatAutoChosen = false;
  if (vatIndicator === '' && vario.vatIndicator !== null) {
    const bekannt = vatIndicators.some((v) => v.vat_indicator === vario.vatIndicator);
    if (bekannt) {
      vatIndicator = vario.vatIndicator;
      vatAutoChosen = true;
    }
  }

  const listingPriceCents = pickNumberOverride(existingOverrides, 'listing_price');
  const amountValue = pickNumberOverride(existingOverrides, 'amount');
  const minimumPriceCents = pickNumberOverride(existingOverrides, 'minimum_price');

  const listingPriceEuro = listingPriceCents !== null
    ? (listingPriceCents / 100).toFixed(2)
    : (vario.listingPriceEur !== null ? vario.listingPriceEur.toFixed(2) : '');
  const minimumPriceEuro = minimumPriceCents !== null
    ? (minimumPriceCents / 100).toFixed(2)
    : (vario.minimumPriceEur !== null ? vario.minimumPriceEur.toFixed(2) : '');
  const amount = amountValue !== null
    ? String(amountValue)
    : (vario.amount !== null ? String(vario.amount) : '');

  return {
    condition,
    listingPriceEuro,
    amount,
    handlingTime: String(handlingTime),
    idShippingGroup,
    vatIndicator,
    vatAutoChosen,
    vatAutoRate: vatAutoChosen ? vario.vatRate : null,
    minimumPriceEuro,
    note: pickStringOverride(existingOverrides, 'note') ?? '',
    hints: {
      ...(listingPriceCents === null && vario.hints.listing_price ? { listing_price: vario.hints.listing_price } : {}),
      ...(amountValue === null && vario.hints.amount ? { amount: vario.hints.amount } : {}),
      ...(minimumPriceCents === null && vario.hints.minimum_price ? { minimum_price: vario.hints.minimum_price } : {}),
    },
  };
}

// ---------------------------------------------------------------------------
// Markup
// ---------------------------------------------------------------------------

export interface OfferFormRenderOptions {
  editable: boolean;
  shippingGroups: readonly ShippingGroupRow[];
  vatIndicators: readonly VatIndicatorRow[];
  /**
   * Anzeigename der Storefront, ausschliesslich fuer den Hinweis bei leerer
   * Kennzeichen-Liste. Ohne ihn stuende dort ein Pflichtfeld ohne eine einzige Option
   * und ohne Erklaerung -- der Zustand, der am 2026-08-25 die Diagnose gekostet hat.
   */
  storefrontLabel: string;
  /**
   * Fertiges Markup neben dem Titel „Verkaufspreis": der Klick-Knopf „xx,xx € übernehmen"
   * aus views/shared/price-takeover.ts oder ein stummer Hinweis. Der Aufrufer baut es,
   * weil der Vorschlag aus einer Edge Function kommt und dieses Modul netzfrei bleibt.
   */
  priceTakeoverHtml?: string;
  /** Dasselbe fuer den Lagerbestand: „N verfügbar übernehmen" neben dem Titel. */
  stockTakeoverHtml?: string;
}

/**
 * Die acht Feldzeilen, OHNE <form>-Huelle und ohne Footer. Beide Aufrufer bauen
 * ihre eigene Huelle, weil sie unterschiedliche Fussleisten haben (Aufschalten
 * plus „Doch neu listen" gegen einen einzelnen Listen-Knopf). Die Felder selbst
 * sind identisch und liegen deshalb nur hier.
 *
 * Styling kommt aus den vorhandenen `klst-attach-form`-Regeln in bridge.css. Der
 * Klassenname traegt „attach" aus historischen Gruenden; er stylt die
 * Verkaufsbedingungen, nicht den Modus. Keine neue Klasse, kein neues Praefix.
 */
export function renderOfferFormFields(p: OfferFormPrefill, opts: OfferFormRenderOptions): string {
  const disabled = opts.editable ? '' : 'disabled';
  const hint = (text: string | undefined): string =>
    text ? `<div class="klst-attach-hint">Aus Vario: ${escapeHtml(text)}</div>` : '';

  return `
        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Kondition</div>
          <div class="kl-settings-control">
            <div class="v-select" style="flex:1;">
              <select name="condition" ${disabled}>
                ${CONDITION_OPTIONS.map((o) => `<option value="${escapeHtml(o.value)}" ${o.value === p.condition ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Verkaufspreis (EUR)${opts.priceTakeoverHtml ?? ''}</div>
          <div class="kl-settings-control">
            <input type="number" step="0.01" min="0" name="listing_price" required
                   value="${escapeHtml(p.listingPriceEuro)}" placeholder="z.B. 19.90" ${disabled} />
            ${hint(p.hints.listing_price)}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Lagerbestand${opts.stockTakeoverHtml ?? ''}</div>
          <div class="kl-settings-control">
            <input type="number" step="1" min="0" name="amount" required
                   value="${escapeHtml(p.amount)}" placeholder="z.B. 10" ${disabled} />
            ${hint(p.hints.amount)}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Lieferzeit (Tage)</div>
          <div class="kl-settings-control">
            <input type="number" step="1" min="0" name="handling_time" required
                   value="${escapeHtml(p.handlingTime)}" placeholder="z.B. 2" ${disabled} />
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Versandgruppe</div>
          <div class="kl-settings-control">
            <div class="v-select" style="flex:1;">
              <select name="id_shipping_group" required ${disabled}>
                <option value="" ${p.idShippingGroup === '' ? 'selected' : ''}>— bitte waehlen —</option>
                ${opts.shippingGroups.map((g) => `<option value="${escapeHtml(g.id_shipping_group)}" ${g.id_shipping_group === p.idShippingGroup ? 'selected' : ''}>${escapeHtml(g.name ?? g.id_shipping_group)}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">MwSt-Indikator</div>
          <div class="kl-settings-control">
            ${opts.vatIndicators.length === 0 ? `
              <div class="klst-attach-hint">
                Fuer die Storefront ${escapeHtml(opts.storefrontLabel)} ist kein MwSt-Kennzeichen
                bekannt. Der Kennzeichen-Katalog wird woechentlich von Kaufland abgeglichen —
                bitte melde Dich, wenn das laenger so bleibt.
              </div>
            ` : `
              <div class="v-select" style="flex:1;">
                <select name="vat_indicator" required ${disabled}>
                  <option value="" ${p.vatIndicator === '' ? 'selected' : ''}>— bitte waehlen —</option>
                  ${opts.vatIndicators.map((v) => {
                    const lbl = v.label ?? v.vat_indicator;
                    const rate = v.vat_rate !== null ? ` (${v.vat_rate}%)` : '';
                    return `<option value="${escapeHtml(v.vat_indicator)}" ${v.vat_indicator === p.vatIndicator ? 'selected' : ''}>${escapeHtml(lbl)}${rate}</option>`;
                  }).join('')}
                </select>
              </div>
              ${p.vatAutoChosen ? `<div class="klst-attach-hint">Aus Vario-Steuerschema${p.vatAutoRate !== null ? ` (${p.vatAutoRate}%)` : ''}</div>` : ''}
            `}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Mindestpreis (EUR, optional)</div>
          <div class="kl-settings-control">
            <input type="number" step="0.01" min="0" name="minimum_price"
                   value="${escapeHtml(p.minimumPriceEuro)}" placeholder="leer = kein Mindestpreis" ${disabled} />
            ${hint(p.hints.minimum_price)}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Kunden-Hinweis (optional)</div>
          <div class="kl-settings-control">
            <textarea name="note" rows="2" maxlength="${NOTE_MAX_LENGTH}"
                      placeholder="z. B. Gebrauchsspuren oder fehlendes Zubehoer" ${disabled}>${escapeHtml(p.note)}</textarea>
            <div class="klst-attach-hint">
              Steht bei Kaufland auf der Produktseite, sichtbar bei gebrauchten Artikeln
              (Kondition ungleich „Neu"). Kein interner Vermerk. Hoechstens ${NOTE_MAX_LENGTH} Zeichen.
            </div>
          </div>
        </div>`;
}

// ---------------------------------------------------------------------------
// Lesen + Validieren
// ---------------------------------------------------------------------------

export interface OfferFormRaw {
  condition: string;
  listing_price: string;
  amount: string;
  handling_time: string;
  id_shipping_group: string;
  vat_indicator: string;
  minimum_price: string;
  note: string;
}

/** Overrides in genau der Form, die kaufland_save_attach_overrides speichert und
 *  der Worker liest. Preise in CENT. */
export interface OfferOverrides {
  condition: string;
  listing_price: number;
  amount: number;
  handling_time: number;
  id_shipping_group: string;
  vat_indicator: string;
  minimum_price: number | null;
  note: string | null;
}

export type ParseOfferResult =
  | { ok: true; overrides: OfferOverrides }
  | { ok: false; error: string };

/**
 * Validiert die Rohwerte des Formulars und rechnet die beiden Preise in Cent um.
 *
 * Rein, damit die Regeln ohne DOM und ohne Backend pruefbar sind. Sie spiegeln die
 * Pflichtfelder des Workers (kaufland-publish-listing.ts, Abschnitt 4), sind aber
 * strenger: der Worker akzeptiert bei amount auch gebrochene Zahlen und laesst
 * Versandgruppe und MwSt aus den Offer-Defaults fallen. Hier muessen beide im
 * Formular stehen, denn sie sind aus genau diesen Defaults vorbelegt. Ein leeres Feld
 * heisst also wirklich „nirgends gepflegt".
 */
export function parseOfferOverrides(raw: OfferFormRaw): ParseOfferResult {
  const condition = raw.condition.trim();
  if (condition === '') return { ok: false, error: 'Kondition fehlt.' };

  const listingPriceNum = Number(raw.listing_price.trim());
  if (raw.listing_price.trim() === '' || !Number.isFinite(listingPriceNum) || listingPriceNum <= 0) {
    return { ok: false, error: 'Verkaufspreis fehlt oder ungueltig.' };
  }

  const amountNum = Number(raw.amount.trim());
  if (raw.amount.trim() === '' || !Number.isFinite(amountNum) || amountNum < 0 || !Number.isInteger(amountNum)) {
    return { ok: false, error: 'Lagerbestand fehlt oder ungueltig.' };
  }

  const handlingTimeNum = Number(raw.handling_time.trim());
  if (raw.handling_time.trim() === '' || !Number.isFinite(handlingTimeNum) || handlingTimeNum < 0 || !Number.isInteger(handlingTimeNum)) {
    return { ok: false, error: 'Lieferzeit fehlt oder ungueltig.' };
  }

  const idShippingGroup = raw.id_shipping_group.trim();
  if (idShippingGroup === '') return { ok: false, error: 'Versandgruppe fehlt.' };

  const vatIndicator = raw.vat_indicator.trim();
  if (vatIndicator === '') return { ok: false, error: 'MwSt-Indikator fehlt.' };

  let minimumPriceCents: number | null = null;
  const minimumPriceRaw = raw.minimum_price.trim();
  if (minimumPriceRaw !== '') {
    const n = Number(minimumPriceRaw);
    if (!Number.isFinite(n) || n <= 0) return { ok: false, error: 'Mindestpreis ungueltig.' };
    minimumPriceCents = Math.round(n * 100);
  }

  const note = raw.note.trim();
  if (note.length > NOTE_MAX_LENGTH) {
    return { ok: false, error: `Kunden-Hinweis ist laenger als ${NOTE_MAX_LENGTH} Zeichen.` };
  }

  return {
    ok: true,
    // STRIKT snake-case Keys, der Worker liest exakt diese Namen.
    overrides: {
      condition,
      listing_price: Math.round(listingPriceNum * 100),
      amount: amountNum,
      handling_time: handlingTimeNum,
      id_shipping_group: idShippingGroup,
      vat_indicator: vatIndicator,
      minimum_price: minimumPriceCents,
      note: note.length > 0 ? note : null,
    },
  };
}

/** Sucht das Verkaufsbedingungen-Formular im uebergebenen Teilbaum. Einziger Weg,
 *  auf den der Kopfleisten-Knopf zugreift. Er kennt die IDs der beiden Aufrufer
 *  bewusst nicht. */
export function findOfferForm(root: ParentNode): HTMLFormElement | null {
  return root.querySelector<HTMLFormElement>(OFFER_FORM_SELECTOR);
}

/** Duenner DOM-Leser: FormData rein, Rohwerte raus. Die Pruefung passiert in
 *  parseOfferOverrides(), damit sie ohne Browser testbar bleibt. */
export function readOfferFormRaw(form: HTMLFormElement): OfferFormRaw {
  const fd = new FormData(form);
  const feld = (name: string): string => {
    const v = fd.get(name);
    return typeof v === 'string' ? v.trim() : '';
  };
  return {
    condition: feld('condition'),
    listing_price: feld('listing_price'),
    amount: feld('amount'),
    handling_time: feld('handling_time'),
    id_shipping_group: feld('id_shipping_group'),
    vat_indicator: feld('vat_indicator'),
    minimum_price: feld('minimum_price'),
    note: feld('note'),
  };
}

// ---------------------------------------------------------------------------
// Aufloesung aus dem Mapping (mode='new')
// ---------------------------------------------------------------------------

export type OfferFieldKey =
  | 'condition' | 'listing_price' | 'amount' | 'handling_time'
  | 'id_shipping_group' | 'vat_indicator' | 'minimum_price';

/** Beschriftungen fuer Meldungen. Die Schluessel sind die Kaufland-Feldnamen, unter denen
 *  der Haendler sie im Reiter „Mapping" wiederfindet, deshalb stehen sie mit dabei. */
/** Muss dieselbe Locale liefern wie der Vorschau-Reiter, sonst findet die Profilsuche ein
 *  anderes Profil als das, was der Haendler dort sieht. Seit 2026-08-16 garantiert das die
 *  gemeinsame Quelle in `lib/storefront-locale.ts`; der Re-Export bleibt, damit die
 *  bestehenden Aufrufer dieses Moduls unveraendert weiterlaufen. */
export { localeForStorefront } from '../../lib/storefront-locale.js';

export const OFFER_FIELD_LABELS: Record<OfferFieldKey, string> = {
  condition: 'Zustand',
  listing_price: 'Verkaufspreis',
  amount: 'Lagerbestand',
  handling_time: 'Lieferzeit',
  id_shipping_group: 'Versandgruppe',
  vat_indicator: 'MwSt-Kennzeichen',
  minimum_price: 'Mindestpreis',
};

/** Liest eine Zahl aus dem, was die Mapping-Aufloesung liefert. Werte kommen je nach
 *  Quelle als number ODER als numerischer String (fixed_value ist Text), beides ist
 *  gueltig. Alles andere ist "nicht gesetzt". */
function zahl(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function text(v: unknown): string | null {
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

export type ResolveOfferResult =
  | { ok: true; overrides: Record<string, unknown> }
  | { ok: false; missing: OfferFieldKey[] };

/**
 * Macht aus dem `unitPayload` der Vorschau-Function die offerOverrides.
 *
 * Das Mapping IST die Konfiguration: Preis, Bestand, Zustand, Lieferzeit, Versandgruppe,
 * MwSt-Kennzeichen und Mindestpreis stehen alle als `field_group='offer'` im Mapping-Profil
 * und werden von derselben Kette aufgeloest, die auch die Vorschau zeigt. Deshalb wird hier
 * NICHTS neu aufgeloest, nur uebersetzt.
 *
 * Einheiten: `listing_price` und `minimum_price` kommen bereits in CENT (die Auto-Quelle
 * `auto.listing_price` liefert `grossPriceCents`), genau wie der Worker sie erwartet. Hier
 * wird deshalb NICHT noch einmal mal 100 gerechnet.
 *
 * Pflicht sind nur `listing_price` und `amount`. `id_shipping_group` und `vat_indicator`
 * duerfen aus den Offer-Defaults fallen, exakt wie im Worker (kaufland-publish-listing.ts,
 * Abschnitt 4); wer sie ebenfalls als pflichtig prueft, sperrt korrekt konfigurierte
 * Listings aus. Alles Uebrige wird nur durchgereicht, wenn es da ist, damit der Worker
 * seine eigenen Defaults anwenden kann.
 */
export function resolveOfferOverridesFromUnit(
  unit: Record<string, unknown>,
  defaults: { id_shipping_group?: string | null; vat_indicator?: string | null } | null | undefined,
): ResolveOfferResult {
  const missing: OfferFieldKey[] = [];

  const preisCent = zahl(unit['listing_price']);
  if (preisCent === null || preisCent <= 0) missing.push('listing_price');

  const menge = zahl(unit['amount']);
  if (menge === null || menge < 0 || !Number.isInteger(menge)) missing.push('amount');

  const versand = text(unit['id_shipping_group']) ?? text(defaults?.id_shipping_group);
  if (!versand) missing.push('id_shipping_group');

  const mwst = text(unit['vat_indicator']) ?? text(defaults?.vat_indicator);
  if (!mwst) missing.push('vat_indicator');

  if (missing.length > 0) return { ok: false, missing };

  const overrides: Record<string, unknown> = {
    listing_price: Math.round(preisCent as number),
    amount: menge as number,
    id_shipping_group: versand,
    vat_indicator: mwst,
  };

  // Optionales nur setzen, wenn das Mapping wirklich etwas liefert. Ein leerer Schluessel
  // im Snapshot wuerde den Default des Workers ueberschreiben statt ihn zuzulassen.
  const zustand = text(unit['condition']);
  if (zustand) overrides['condition'] = zustand;

  const lieferzeit = zahl(unit['handling_time']);
  if (lieferzeit !== null && Number.isInteger(lieferzeit) && lieferzeit >= 0) {
    overrides['handling_time'] = lieferzeit;
  }

  const mindestpreis = zahl(unit['minimum_price']);
  if (mindestpreis !== null && mindestpreis > 0) overrides['minimum_price'] = Math.round(mindestpreis);

  const notiz = text(unit['note']);
  if (notiz) overrides['note'] = notiz;

  return { ok: true, overrides };
}

/**
 * Baut die Meldung fuer fehlende Pflichtwerte.
 *
 * Das ist der EINZIGE Ort, an dem der Haendler von einer fehlenden Zuordnung erfaehrt, seit
 * es kein Eingabeformular mehr gibt. Deshalb wird jedes Feld beim Namen genannt und gesagt,
 * WO es zugeordnet wird.
 *
 * `diagnosen` sind die `errors[].message` der Vorschau-Function zu genau diesen Feldern. Sie
 * sind deutlich konkreter als alles, was hier stehen koennte ("Storefront in den Einstellungen
 * noch kein Vario-Lager zugeordnet", "Weder Verkaufskanal-Preis noch Stammdaten-Preis am
 * Artikel gepflegt") und haben deshalb Vorrang.
 */
export function missingOfferFieldsMessage(
  missing: readonly OfferFieldKey[],
  diagnosen: readonly string[] = [],
): string {
  const felder = missing.map((k) => `${OFFER_FIELD_LABELS[k]} (${k})`).join(', ');
  const kopf = missing.length === 1
    ? `${felder} fehlt.`
    : `Diese Angaben fehlen: ${felder}.`;
  if (diagnosen.length > 0) return `${kopf} ${diagnosen.join(' ')}`;
  return `${kopf} Bitte im Reiter „Mapping" zuordnen und speichern, dann erneut listen.`;
}

// ---------------------------------------------------------------------------
// Sperre gegen doppeltes Absenden
// ---------------------------------------------------------------------------

export interface OfferSubmitLock {
  /** Haelt gerade jemand die Sperre? */
  readonly held: boolean;
  /** true, wenn die Sperre erworben wurde. false heisst: laeuft schon, nichts tun. */
  tryAcquire(): boolean;
  /** Gibt frei. Mehrfaches Freigeben ist folgenlos. */
  release(): void;
}

/**
 * Eine Sperre fuer ALLE Absende-Knoepfe eines Listings, nicht eine pro Knopf.
 *
 * Bei mode='new' gibt es zwei Knoepfe auf dasselbe Formular: „Listen" in der
 * Kopfleiste und „Jetzt auf Kaufland listen" im Abschnitt. Jeder deaktivierte
 * bisher nur sich selbst, der andere blieb waehrend des laufenden Requests
 * klickbar.
 *
 * Das ist nicht kosmetisch. `kaufland_save_attach_overrides` liest den Status per
 * SELECT und schreibt per separatem UPDATE, ohne Sperre; zwei fast gleichzeitige
 * Aufrufe sehen beide einen gueltigen Status. Danach ruft jeder
 * `kaufland_enqueue_listing_publish`, dessen Idempotenz-Pruefung ebenfalls ein
 * ungesperrtes SELECT-dann-INSERT ist: beide koennen „kein offener Job" sehen,
 * bevor einer eingefuegt hat. Ergebnis waeren zwei publish_listing-Jobs und damit
 * zwei Produktdaten-Einreichungen bei Kaufland, fuer die es kein DELETE gibt.
 *
 * Die serverseitige Race in Migration 0027 bleibt bestehen, die ist ein eigenes
 * Thema. Diese Sperre schliesst den Weg, auf dem sie ueberhaupt erst erreichbar
 * wurde.
 *
 * `tryAcquire()` MUSS synchron vor dem ersten `await` gerufen werden, sonst
 * schluepft der zweite Klick durch die Luecke, die man gerade schliessen wollte.
 *
 * `onChange` meldet jeden Wechsel. Daran haengt der Aufrufer das Deaktivieren
 * BEIDER Knoepfe, nicht nur des geklickten.
 */
export function createOfferSubmitLock(onChange?: (held: boolean) => void): OfferSubmitLock {
  let held = false;
  return {
    get held() { return held; },
    tryAcquire(): boolean {
      if (held) return false;
      held = true;
      onChange?.(true);
      return true;
    },
    release(): void {
      if (!held) return;
      held = false;
      onChange?.(false);
    },
  };
}

// ---------------------------------------------------------------------------
// Speichern + Einreihen
// ---------------------------------------------------------------------------

export interface OfferSubmitDeps {
  /** kaufland_save_attach_overrides */
  saveOverrides(overrides: Record<string, unknown>): Promise<{ error: { message: string } | null }>;
  /** kaufland_enqueue_listing_publish */
  enqueuePublish(): Promise<{ error: { message: string } | null }>;
  /** Fortschritt fuer die Statuszeile. Optional, rein kosmetisch. */
  onStage?(stage: 'save' | 'enqueue'): void;
}

export type OfferSubmitResult =
  | { ok: true }
  | { ok: false; stage: 'validate' | 'save' | 'enqueue'; message: string };

/**
 * Ein Klick: pruefen, speichern, einreihen. Keine separate Bestaetigung, weil der
 * Haendler schnell listen koennen soll.
 *
 * Die Reihenfolge ist die eigentliche Zusage dieser Funktion und wird getestet:
 * Speichern ZUERST, Einreihen nur bei Erfolg. Andersherum liefe der Worker gegen
 * die alten Overrides und pushte Werte, die der Haendler gerade ueberschrieben hat.
 * Aus demselben Grund gibt es kein Einreihen nach fehlgeschlagener Pruefung.
 */
export async function saveAndEnqueueOffer(
  raw: OfferFormRaw,
  deps: OfferSubmitDeps,
): Promise<OfferSubmitResult> {
  const parsed = parseOfferOverrides(raw);
  if (!parsed.ok) return { ok: false, stage: 'validate', message: parsed.error };
  return saveAndEnqueueOverrides({ ...parsed.overrides }, deps);
}

/**
 * Dasselbe ohne Formular: fuer den Listen-Knopf bei mode='new', der seine Werte aus dem
 * Mapping aufloest statt aus Eingabefeldern. Die Reihenfolge-Zusage von oben gilt hier
 * genauso, sie steht bewusst nur an dieser einen Stelle.
 */
export async function saveAndEnqueueOverrides(
  overrides: Record<string, unknown>,
  deps: OfferSubmitDeps,
): Promise<OfferSubmitResult> {
  deps.onStage?.('save');
  const sav = await deps.saveOverrides(overrides);
  if (sav.error) return { ok: false, stage: 'save', message: sav.error.message };

  deps.onStage?.('enqueue');
  const enq = await deps.enqueuePublish();
  if (enq.error) return { ok: false, stage: 'enqueue', message: enq.error.message };

  return { ok: true };
}

// ---------------------------------------------------------------------------
// Kleinkram
// ---------------------------------------------------------------------------

function pickStringOverride(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function pickNumberOverride(obj: Record<string, unknown>, key: string): number | null {
  const v = obj[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}

/** Eigene Kopie statt Import aus listing-detail-pane.ts: ein Wert-Import dorthin
 *  waere ein echter Laufzeit-Zyklus, denn jene Datei importiert dieses Modul. */
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
